import { type RefObject, useEffect } from 'react';
import { Swiper as SwiperType } from 'swiper/types';

export interface UseReducedMotionAutoplayOptions {
  swiperRef: RefObject<SwiperType | null>;
  respectReducedMotion: boolean;
  isSwiperReady: boolean;
}

/**
 * Stops autoplay when `prefers-reduced-motion: reduce` is on and resumes it when
 * cleared. Uses `isSwiperReady` (not a ref) so the effect re-runs once Swiper
 * exists on the client.
 */
export function useReducedMotionAutoplay({
  swiperRef,
  respectReducedMotion,
  isSwiperReady,
}: UseReducedMotionAutoplayOptions): void {
  useEffect(() => {
    if (!respectReducedMotion || !isSwiperReady) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const swiper = swiperRef.current;
    if (!swiper?.autoplay) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = (): void => {
      if (mql.matches) {
        swiper.autoplay.stop();
      } else if (swiper.params.autoplay) {
        swiper.autoplay.start();
      }
    };

    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [respectReducedMotion, isSwiperReady, swiperRef]);
}
